import React, { useState } from "react";
import {
	Topbar,
	WalletCard,
	LoginAlert,
	Overlay,
	GameAlert,
} from "@components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	RootContainer,
	Container,
	Tab,
	TabsWrapper,
	SearchInput,
	Col,
	Image,
	Text,
	SpinWrapper,
	Divider,
	EmptyBox,
	NoResultText,
} from "./ChildGames.style";
import { useTranslation } from "react-i18next";
import { Row, Spin } from "antd";
import MaintainCard from "./MaintainCard/MaintainCard";
import { useEffect } from "react";
import { api, Status } from "@api";
import { localStorage } from "@utils";
import { useGame } from "@hooks";
import { saveUser } from "@redux/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { BURMESE_LANGUAGE_CODE_LK, CHINESE_LANGUAGE_CODE_LK } from "@constants";
import { setPrevRoute } from "@redux/game/gameSlice";
import { PUBLIC_ROUTE } from "@router/route.constants";
import { ClearOutlined, NoResultOutlined } from "@assets";

const ChildGames = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { pathname, state: prevRoute } = useLocation();
	const { main_game_id, main_game_name } = useParams();
	const lang = useSelector((state) => state.app.userPreferedLanguage);

	const [child_game_types, setChildGameTypes] = useState([]);
	const [p_type, setType] = useState("");

	const [isMaintain, setIsMaintain] = useState(false);
	const [maintain_start, setMaintainStart] = useState("");
	const [maintain_end, setMaintainEnd] = useState("");

	const [loading, setLoading] = useState(true);
	const [child_games, setChildGames] = useState([]);

	const [searchKey, setSearchKey] = useState("");
	let timer = null;

	const { gameExitStatus } = useSelector((state) => state.game);
	const { launchGame, exitGame } = useGame();

	useEffect(() => {
		if (gameExitStatus === Status.SUCCESS) {
			api
				.getChildGames(main_game_id)
				.then(({ user }) => {
					updateUserInApp(user);
				})
				.catch(() => {});
		}

		// eslint-disable-next-line
	}, [gameExitStatus]);

	useEffect(() => {
		getChildGames();

		// eslint-disable-next-line
	}, []);

	const getChildGames = () => {
		setLoading(true);
		api
			.getChildGames(main_game_id)
			.then(({ main_game, user }) => {
				const {
					active,
					maintain_start,
					maintain_end,
					child_game_types,
					child_games,
				} = main_game;

				setLoading(false);

				setChildGameTypes(child_game_types);
				setChildGames(parseData(child_games));

				setIsMaintain(!active && maintain_start);
				setMaintainStart(maintain_start);
				setMaintainEnd(maintain_end);

				updateUserInApp(user);
			})
			.catch(() => {
				setLoading(false);
				goToGames();
			});
	};

	/**
	 * get game names and icons
	 * based on current app language
	 */
	const parseData = (games) => {
		return games.map((item) => {
			return {
				...item,
				icon:
					lang === CHINESE_LANGUAGE_CODE_LK
						? item.icon_ch
						: lang === BURMESE_LANGUAGE_CODE_LK
						? item.icon_mm
						: item.icon_en,
				name:
					lang === CHINESE_LANGUAGE_CODE_LK
						? item.name_ch
						: lang === BURMESE_LANGUAGE_CODE_LK
						? item.name_mm
						: item.name_en,
			};
		});
	};

	/**
	 * Update user in app
	 * Redux & Localstorage
	 */
	const updateUserInApp = (user) => {
		if (user) {
			// Save user to redux
			dispatch(saveUser(user));
			// Set user to localstorage
			localStorage.setUser(user);

			// if current user is ingame, exit
			user.is_in_game && exitGame();
		}
	};

	const handleGameClicked = (game) => {
		/**
		 * Save pathname to redux,
		 * to use from ingame page for going back
		 **/
		dispatch(setPrevRoute(pathname));
		/**
		 * Launch game
		 */
		launchGame(game);
	};

	const goToGames = () => {
		navigate(`/${PUBLIC_ROUTE.HOME}/${PUBLIC_ROUTE.GAMES}`);
	};

	const handleSearchKeyChange = (e) => {
		timer && clearTimeout(timer);
		let value = e.target.value;
		if (value) {
			timer = setTimeout(() => {
				setSearchKey(value);
			}, 1000);
		} else {
			setSearchKey(value);
		}
	};

	const goBack = () => {
		navigate(prevRoute || `/${PUBLIC_ROUTE.HOME}/${PUBLIC_ROUTE.GAMES}`);
	};

	const Tabs = () => (
		<TabsWrapper>
			{child_game_types?.map((item, key) => (
				<Tab
					key={key}
					status={item.p_type === p_type ? "active" : "default"}
					onClick={() => setType(item.p_type)}
				>
					{item.name}
				</Tab>
			))}
		</TabsWrapper>
	);

	const Loader = () => (
		<SpinWrapper>
			<Spin />
		</SpinWrapper>
	);

	const EmptyCard = () => (
		<EmptyBox>
			<NoResultOutlined />
			<NoResultText>{t("common.noResult")}</NoResultText>
		</EmptyBox>
	);

	// Filter based on p_type condition
	const filteredByPType = child_games.filter(
		(game) => game.p_type === p_type || !p_type
	);

	// Filter based on searchKey condition
	const filteredBySearchKey = filteredByPType.filter(
		(game) =>
			game.name.toLowerCase().includes(searchKey.toLowerCase()) || !searchKey
	);

	return (
		<RootContainer>
			<Topbar title={main_game_name} goBack={goBack} />
			<Container>
				<WalletCard />
			</Container>
			{loading && <Loader />}
			{isMaintain && (
				<MaintainCard
					maintain_start={maintain_start}
					maintain_end={maintain_end}
				/>
			)}
			{!isMaintain && !loading && (
				<>
					<Divider />
					<Tabs />
					<Container>
						{child_games.length > 0 && (
							<SearchInput
								allowClear={{
									clearIcon: <ClearOutlined />,
								}}
								placeholder={t("home.searchInputPlaceholder")}
								onChange={handleSearchKeyChange}
							/>
						)}
						{searchKey && filteredBySearchKey.length === 0 ? (
							<EmptyCard />
						) : (
							<Row gutter={[40, 16]}>
								{filteredBySearchKey.map((game, key) => (
									<Col key={key}>
										<div>
											<Image
												src={game.icon}
												onClick={() => handleGameClicked(game)}
											/>
										</div>
										<Text onClick={() => handleGameClicked(game)}>
											{game.name}
										</Text>
									</Col>
								))}
							</Row>
						)}
					</Container>
				</>
			)}
			<LoginAlert />
			<Overlay />
			<GameAlert />
		</RootContainer>
	);
};

export default ChildGames;
